import { useEffect, useState } from "react";
import useCopyClipboard from "react-use-clipboard";
import { CopyIcon } from "../../icons";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { getScheduledPodcastsByMeetingId } from "../../../Services/SchedulesApi";
import { commonConstants } from "../../../utils/constant";
import { formatGuestName } from "../../utils/common";
import "./MeetingDetailModal.css";

export default function MeetingDetailModal({
  setName,
  nameErr,
  setNameErr,
  startMeeting,
  meetingTitle,
  meetingUrl,
  appTheme,
}) {
  const { t } = useTranslation();
  
  const [copied, setCopied] = useCopyClipboard(meetingUrl, {
    successDuration: 4000,
  });

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const showName = queryParams.get("showName");
  const host = queryParams.get("host");
  const guest = queryParams.get("guest");
  const meetingId = queryParams.get("meetingId");
  
  const [value, setValue] = useState('1');
  const [schedule, setSchedule] = useState();
  const [localName, setLocalName] = useState(() => localStorage.getItem('fullName') || '');
  const [role, setRole] = useState("");

  useEffect(() => {
    const savedName = localStorage.getItem('fullName');
    if (savedName) {
      setLocalName(savedName);
      setName(savedName);
    }
  }, []);

  const handleChange = (newValue) => {
    setValue(newValue);
  };

  useEffect(() => {
    const fetchSchedule = async () => {
      try {
        // Fetch podcast details
        const response = await getScheduledPodcastsByMeetingId(meetingId); // API call to fetch podcasts
        setSchedule(response?.data?.Data[0]); // Update podcasts data

      } catch (error) {
        console.error("Error fetching podcasts:", error); // Error handling
      } finally {
        // setLoading(false); // End loading after the fetch is complete
      }
    };

    fetchSchedule();
  }, []);

  const handleValidation = async () => {
    let formatedName = await formatGuestName(localName, role);
    setName(formatedName);
    let isValid = true;
    if (formatedName.length < 3) {
      isValid = false;
      setNameErr(true);
      return false;
    } else {
      setNameErr(false);
    }
    return isValid;
  };


  const renderImportantLinks = (linksString) => {
    if (!linksString?.trim()) {
      return <span>{commonConstants.NO_IMP_LINK || 'The important links have not been provided.'}</span>;
    }

    const links = linksString
      .trim()
      .split(',')
      .map(link => link.trim())
      .filter(link => link !== '');

    const validLinks = links.reduce((acc, link) => {
      try {
        const urlStr = link.startsWith('http') ? link : `https://${link}`;
        const url = new URL(urlStr);

        acc.push(
          <li key={urlStr} style={{ marginBottom: '6px' }}>
            <a
              href={url.href}
              target="_blank"
              rel="noopener noreferrer"
              title={link}
            >
              {url.hostname}
            </a>
          </li>
        );
      } catch (error) {
        console.warn(`Invalid URL: ${link}`, error);
      }
      return acc;
    }, []);

    return validLinks.length > 0 ? (
      <ul style={{ paddingLeft: '20px', margin: 0 }}>
        {validLinks}
      </ul>
    ) : (
      <span>{commonConstants.NO_IMP_LINK || 'No valid links found.'}</span>
    );
  };

  return (
    <div className="meeting-detail-container">
      {(meetingUrl && meetingTitle) || meetingUrl ? (
        <div className="meeting-card">
          {meetingTitle && (
            <div className="meeting-title">
              {meetingTitle}
            </div>
          )}

          {meetingUrl && (
            <div className="meeting-url-box">
              <div className="meeting-url-text">
                {meetingUrl}
              </div>
              <button
                className="copy-button"
                onClick={(e) => setCopied(e)}
                title={copied ? "Copied" : "Copy"}
              >
                <CopyIcon fill="#fff" />
              </button>
            </div>
          )}
        </div>
      ) : meetingTitle ? (
        <div className="meeting-title">
          {meetingTitle}
        </div>
      ) : null}

      <div className="form-section">
        <div>
          <label htmlFor="show-name-input" className="input-label">Show Name</label>
          <input
            id="show-name-input"
            type="text"
            className="input-field"
            placeholder={t("Show Name")}
            value={showName || ''}
            disabled={true}
          />
        </div>

        <div>
          <label htmlFor="user-name-input" className="input-label">Enter your name</label>
          <input
            id="user-name-input"
            type="text"
            className="input-field"
            placeholder={t("Enter your name")}
            value={localName || ''}
            onChange={(ev) => {
              setLocalName(ev.target.value);
              setName(ev.target.value);
            }}
            style={nameErr ? { borderColor: '#f44336' } : {}}
          />
        </div>

        <div>
          <label htmlFor="designation-input" className="input-label">Designation</label>
          <input
            id="designation-input"
            type="text"
            className="input-field"
            placeholder={t("Enter your Designation")}
            value={role}
            onChange={(e) => {
              setRole(e.target.value);
            }}
          />
        </div>

        {nameErr && (
          <p className="error-text">
            {t("Enter Minimum 3 Characters")}
          </p>
        )}

        <button
          className="join-button"
          onClick={(e) => {
            const isValid = handleValidation();
            if (isValid) {
              startMeeting(e);
            }
          }}
          id="btnJoin"
        >
          {t("Join Now")}
        </button>
      </div>

      <div className="tabs-container">
        <button
          className={`tab-button ${value === '1' ? 'active' : ''}`}
          onClick={() => handleChange('1')}
        >
          Agenda
        </button>
        <button
          className={`tab-button ${value === '2' ? 'active' : ''}`}
          onClick={() => handleChange('2')}
        >
          Guest Details
        </button>
        <button
          className={`tab-button ${value === '3' ? 'active' : ''}`}
          onClick={() => handleChange('3')}
        >
          Important links
        </button>
      </div>

      <div className="tab-content">
        {value === "1" && (schedule?.Agenda ? (
          <div dangerouslySetInnerHTML={{ __html: schedule?.Agenda }} />
        ) : commonConstants.NO_AGENDA)}
        {value === "2" && (schedule?.GuestInfo ? (
          <div dangerouslySetInnerHTML={{ __html: schedule?.GuestInfo }} />
        ) : 'The guest info has not been provided.')}
        {value === "3" && (
          <div>
            {renderImportantLinks(schedule?.ImportantLinks)}
          </div>
        )}
      </div>
    </div>
  );
}
